// Schnelleingabe für Aufgaben: eine Zeile, wie man sie einem Kollegen zuruft.
//
//   „Morgen Meyer anrufen !hoch @Mia"
//     → Titel „Meyer anrufen", fällig morgen, Priorität hoch, zuständig Mia
//
// Erkannt werden:
//   heute · morgen · übermorgen (auch today/tomorrow und die Wörter der
//   eingestellten Sprache), Wochentage in der eingestellten Sprache, auf
//   Deutsch und Englisch, 15.11. / 15.11.2026 / 2026-11-15,
//   !hoch !high !niedrig !low (und die Wörter der Sprache), @Name,
//   Uhrzeiten 14:30 · 9:00 · 14 Uhr · 14:30-15:15 (dann ist es ein Termin).
// Alles andere bleibt Titel. Was erkannt wurde, zeigt die Oberfläche vor dem
// Anlegen an — niemand soll raten müssen, warum ein Wort verschwunden ist.

export type Erkannt = {
  titel: string;
  dueDate?: string;
  priority?: 'high' | 'low';
  assigneeId?: string;
  assigneeName?: string;
  uhrzeit?: string;
  bisUhrzeit?: string;
};

const zweistellig = (h: string, m = '00') => `${h.padStart(2, '0')}:${m}`;

export type Person = { id: string; name: string; username: string };

/** YYYY-MM-DD im lokalen Kalender (nicht UTC — sonst ist kurz nach Mitternacht noch gestern). */
export function tagText(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function plusTage(d: Date, n: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
  x.setDate(x.getDate() + n);
  return x;
}

const klein = (s: string) => s.toLocaleLowerCase().normalize('NFC');

/**
 * Wochentagsnamen in den gewünschten Sprachen → 0 = Sonntag. Kurzformen nur
 * mit Punkt („Fr.") — „so", „mi", „do" sind sonst ganz normale Wörter.
 */
function wochentage(sprachen: string[]): Map<string, number> {
  const m = new Map<string, number>();
  // 2026-10-11 ist ein Sonntag.
  for (let i = 0; i < 7; i++) {
    const d = new Date(2026, 9, 11 + i, 12);
    for (const s of sprachen) {
      for (const art of ['long', 'short'] as const) {
        try {
          const name = klein(d.toLocaleDateString(s, { weekday: art })).replace(/\.$/, '');
          if (name.length >= 2) m.set(art === 'short' ? name + '.' : name, i);
        } catch { /* Sprache unbekannt */ }
      }
    }
  }
  return m;
}

export function erkennen(eingabe: string, o: {
  sprache: string;
  personen: Person[];
  /** Wörter der eingestellten Sprache, z. B. aus t('aufgaben.heute'). */
  woerter?: { heute?: string; morgen?: string; hoch?: string; niedrig?: string };
  jetzt?: Date;
}): Erkannt {
  const jetzt = o.jetzt ?? new Date();
  const tage = wochentage([o.sprache, 'de', 'en']);
  const relativ = new Map<string, number>([
    ['heute', 0], ['today', 0], ['morgen', 1], ['tomorrow', 1], ['übermorgen', 2],
  ]);
  if (o.woerter?.heute) relativ.set(klein(o.woerter.heute), 0);
  if (o.woerter?.morgen) relativ.set(klein(o.woerter.morgen), 1);
  const hoch = new Set(['hoch', 'high', 'dringend', 'h', 'urgent']);
  const niedrig = new Set(['niedrig', 'low', 'n', 'l']);
  if (o.woerter?.hoch) hoch.add(klein(o.woerter.hoch));
  if (o.woerter?.niedrig) niedrig.add(klein(o.woerter.niedrig));

  const ergebnis: Erkannt = { titel: '' };
  const rest: string[] = [];

  const woerter = eingabe.trim().split(/\s+/).filter(Boolean);
  for (let i = 0; i < woerter.length; i++) {
    const wort = woerter[i];
    const w = klein(wort).replace(/[,;]$/, '');

    if (!ergebnis.uhrzeit) {
      // 14:30 · 9:00 · 14:30-15:15 · 14uhr · „14 Uhr" (zwei Wörter)
      const zeit = /^([01]?\d|2[0-3]):([0-5]\d)(?:[-–]([01]?\d|2[0-3]):([0-5]\d))?(?:uhr)?$/.exec(w);
      const uhr = /^([01]?\d|2[0-3])uhr$/.exec(w)
        ?? (/^([01]?\d|2[0-3])$/.test(w) && klein(woerter[i + 1] ?? '') === 'uhr' ? [w, w] : null);
      if (zeit) {
        ergebnis.uhrzeit = zweistellig(zeit[1], zeit[2]);
        if (zeit[3] && zweistellig(zeit[3], zeit[4]) > ergebnis.uhrzeit) ergebnis.bisUhrzeit = zweistellig(zeit[3], zeit[4]);
        continue;
      }
      if (uhr) {
        ergebnis.uhrzeit = zweistellig(uhr[1]);
        if (!w.endsWith('uhr')) i++; // „Uhr" gleich mit verbrauchen
        continue;
      }
    }

    if (w.startsWith('!') && w.length > 1) {
      const p = w.slice(1);
      if (hoch.has(p) || /^!+$/.test(w.slice(1))) { ergebnis.priority = 'high'; continue; }
      if (niedrig.has(p)) { ergebnis.priority = 'low'; continue; }
    }

    if (w.startsWith('@') && w.length > 1 && !ergebnis.assigneeId) {
      const gesucht = w.slice(1);
      const treffer = o.personen.filter(p =>
        klein(p.username) === gesucht || klein(p.name).split(/\s+/).some(t => t.startsWith(gesucht)));
      if (treffer.length === 1) {
        ergebnis.assigneeId = treffer[0].id;
        ergebnis.assigneeName = treffer[0].name || treffer[0].username;
        continue;
      }
    }

    if (!ergebnis.dueDate) {
      if (relativ.has(w)) { ergebnis.dueDate = tagText(plusTage(jetzt, relativ.get(w)!)); continue; }
      const tag = tage.get(w) ?? tage.get(w.replace(/\.$/, ''));
      if (tag !== undefined) {
        const ziel = tag;
        // Immer der nächste — „Montag" an einem Montag heißt nächste Woche.
        const abstand = ((ziel - jetzt.getDay() + 7) % 7) || 7;
        ergebnis.dueDate = tagText(plusTage(jetzt, abstand));
        continue;
      }
      const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(w);
      const de = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})?$/.exec(w);
      if (iso || de) {
        let j: number, m: number, t: number;
        if (iso) { j = +iso[1]; m = +iso[2]; t = +iso[3]; }
        else {
          t = +de![1]; m = +de![2];
          j = de![3] ? (de![3].length === 2 ? 2000 + +de![3] : +de![3]) : jetzt.getFullYear();
        }
        const d = new Date(j, m - 1, t, 12);
        if (d.getMonth() === m - 1 && d.getDate() === t) {
          // Ohne Jahr und schon vorbei: dann ist das nächste Jahr gemeint.
          if (de && !de[3] && tagText(d) < tagText(jetzt)) d.setFullYear(j + 1);
          ergebnis.dueDate = tagText(d);
          continue;
        }
      }
    }

    rest.push(wort);
  }

  ergebnis.titel = rest.join(' ').trim();
  return ergebnis;
}
